import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@wundo/ui';
import {
  COSTS_SINCE_NOTE,
  fetchOrganisationUsageCost,
  type UsageCostWindows,
} from '@/lib/usageCost.ts';
import { formatCurrency } from '@/utils/currencyUtils.ts';

const WINDOWS: { key: keyof UsageCostWindows; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'last_7d', label: 'Last 7 days' },
  { key: 'last_30d', label: 'Last 30 days' },
  { key: 'all_time', label: 'All time' },
];

export interface UsageCostDialogProps {
  organisationId: string;
  organisationName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** What one organisation has cost us at our vendors, over the four standard windows. */
export function UsageCostDialog({
  organisationId,
  organisationName,
  open,
  onOpenChange,
}: UsageCostDialogProps) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['organisationUsageCost', organisationId],
    queryFn: () => fetchOrganisationUsageCost(organisationId),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size='md' className='p-0'>
        <DialogHeader>
          <DialogTitle>Cost</DialogTitle>
          <DialogClose />
        </DialogHeader>
        <div className='flex flex-col gap-5 px-5 pt-4 pb-5'>
          {isPending && (
            <p className='flex items-center gap-2 text-sm font-semibold text-ink-muted'>
              <Spinner />
              Loading…
            </p>
          )}
          {isError && (
            <p role='alert' className='text-sm font-semibold text-danger'>
              Failed to load cost
            </p>
          )}
          {data && (
            <section className='flex flex-col gap-2'>
              <h3 className='text-sm font-bold text-ink'>{organisationName}</h3>
              <dl className='grid grid-cols-2 gap-3'>
                {WINDOWS.map((window) => (
                  <div key={window.key} className='rounded-md bg-fill-faint px-3 py-2'>
                    <dt className='text-xs text-ink-muted'>{window.label}</dt>
                    <dd className='text-base font-bold text-ink'>
                      {formatCurrency(data[window.key])}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
          <p className='text-xs text-ink-muted'>{COSTS_SINCE_NOTE}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
